import React from 'react';
import type {
  BulkUpdateResult,
  CellUpdateResult,
} from '../../../../shared/services/quickFillRevitService';
import { BatchOperationDialog } from '../../../../shared/ui/batch';
import type {
  BatchOperationItemResult,
  BatchOperationSummary,
} from '../../../../shared/lib/batchOperations';

export interface QuickFillSaveReviewDialogProps {
  open: boolean;
  previewOnly: boolean;
  contentRef?: React.Ref<HTMLDivElement>;
  warning?: string;
  result: BulkUpdateResult | null;
  successRows: CellUpdateResult[];
  failedRows: CellUpdateResult[];
  onClose: () => void;
  onContinue: () => void;
}

const toBatchItem = (row: CellUpdateResult, index: number): BatchOperationItemResult => ({
  id: `${row.familyType}-${row.parameter}-${index}`,
  label: `${row.familyType} | ${row.parameter} = "${row.value}"`,
  status: row.success ? 'success' : 'failed',
  error: row.error,
});

const toBatchSummary = (result: BulkUpdateResult | null): BatchOperationSummary | null =>
  result
    ? {
        total: result.totalUpdates,
        succeeded: result.successCount,
        failed: result.failureCount,
        items: result.results.map(toBatchItem),
      }
    : null;

export const QuickFillSaveReviewDialog: React.FC<QuickFillSaveReviewDialogProps> = ({
  open,
  previewOnly,
  contentRef,
  warning,
  result,
  successRows,
  failedRows,
  onClose,
  onContinue,
}) => {
  const summary = toBatchSummary(result);

  return (
    <BatchOperationDialog
      open={open}
      title="Review Quick Fill save results"
      previewOnly={previewOnly}
      contentRef={contentRef}
      contentTestId="quickfill-tour-target-save-review"
      warning={warning ? `Continue without full save: ${warning}` : undefined}
      summary={summary}
      successItems={successRows.map(toBatchItem)}
      failedItems={failedRows.map(toBatchItem)}
      onClose={onClose}
      onContinue={onContinue}
    />
  );
};
